import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FileText,
  Plus,
  Search,
  Edit2,
  Trash2,
  Eye,
  RefreshCw,
  Check,
  AlertCircle,
  Sparkles,
  Calendar,
  Clock,
  ChevronLeft,
  Image,
  ImagePlus,
  Save,
  Send,
  X,
  Tag,
} from 'lucide-react';
import { useAdminStore } from '../../store/admin';

const API_URL = import.meta.env.VITE_API_URL || '/api';

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  featured_image: string | null;
  thumbnail_image: string | null;
  author_name: string;
  category: string | null;
  tags: string[];
  status: 'draft' | 'published' | 'archived';
  reading_time: number;
  view_count: number;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  meta_title?: string;
  meta_description?: string;
}

interface BlogCategory {
  id: number;
  name: string;
  slug: string;
  description: string | null;
  post_count: number;
}

type ViewMode = 'list' | 'create' | 'edit';

interface ApiResponse {
  success: boolean;
  data?: {
    posts?: BlogPost[];
    title?: string;
    content?: string;
    excerpt?: string;
    tags?: string[];
    category?: string;
    meta_description?: string;
  };
  error?: string;
}

export default function BlogManagement() {
  const { adminToken } = useAdminStore();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get view mode from URL, default to 'list'
  const viewModeParam = searchParams.get('view') as ViewMode | null;
  const viewMode: ViewMode = viewModeParam && ['list', 'create', 'edit'].includes(viewModeParam)
    ? viewModeParam
    : 'list';

  // Helper function to update view mode in URL
  const setViewMode = useCallback((mode: ViewMode) => {
    const newParams = new URLSearchParams(searchParams);
    if (mode === 'list') {
      newParams.delete('view');
      newParams.delete('editId');
    } else {
      newParams.set('view', mode);
      if (mode !== 'edit') {
        newParams.delete('editId');
      }
    }
    setSearchParams(newParams, { replace: false });
  }, [searchParams, setSearchParams]);
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [selectedPost, setSelectedPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Form state
  const [form, setForm] = useState({
    title: '',
    content: '',
    excerpt: '',
    featured_image: '',
    thumbnail_image: '',
    category: '',
    tags: [] as string[],
    author_name: 'SweetPilot Team',
    meta_title: '',
    meta_description: '',
    status: 'draft' as 'draft' | 'published',
  });

  // AI Generation form
  const [aiForm, setAiForm] = useState({
    topic: '',
    category: '',
    tone: 'professional',
    keywords: '',
  });
  const [showAiModal, setShowAiModal] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [generatingImage, setGeneratingImage] = useState(false);

  const fetchPosts = useCallback(async () => {
    if (!adminToken) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus !== 'all') params.append('status', filterStatus);

      const response = await fetch(`${API_URL}/admin/blog/posts?${params}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data: ApiResponse = await response.json();
      if (data.success && data.data?.posts) {
        setPosts(data.data.posts);
      }
    } catch (err) {
      console.error('Failed to fetch posts:', err);
    } finally {
      setLoading(false);
    }
  }, [adminToken, filterStatus]);

  const fetchCategories = useCallback(async () => {
    if (!adminToken) return;
    try {
      const response = await fetch(`${API_URL}/admin/blog/categories`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      const data: { success: boolean; data: BlogCategory[] } = await response.json();
      if (data.success) {
        setCategories(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  }, [adminToken]);

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);

  // Restore edit state from URL when navigating back
  useEffect(() => {
    const editId = searchParams.get('editId');
    if (viewMode === 'edit' && editId && posts.length > 0) {
      const post = posts.find(p => p.id === parseInt(editId, 10));
      if (post && (!selectedPost || selectedPost.id !== post.id)) {
        setSelectedPost(post);
        setForm({
          title: post.title,
          content: post.content,
          excerpt: post.excerpt || '',
          featured_image: post.featured_image || '',
          thumbnail_image: post.thumbnail_image || '',
          category: post.category || '',
          tags: post.tags || [],
          author_name: post.author_name,
          meta_title: post.meta_title || '',
          meta_description: post.meta_description || '',
          status: post.status === 'archived' ? 'draft' : post.status,
        });
      }
    }
  }, [viewMode, searchParams, posts, selectedPost]);

  useEffect(() => {
    if (success || error) {
      const timer = setTimeout(() => {
        setSuccess(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success, error]);

  const handleCreate = () => {
    setSelectedPost(null);
    setForm({
      title: '',
      content: '',
      excerpt: '',
      featured_image: '',
      thumbnail_image: '',
      category: '',
      tags: [],
      author_name: 'SweetPilot Team',
      meta_title: '',
      meta_description: '',
      status: 'draft',
    });
    setViewMode('create');
  };

  const handleEdit = (post: BlogPost) => {
    setSelectedPost(post);
    setForm({
      title: post.title,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      thumbnail_image: post.thumbnail_image || '',
      category: post.category || '',
      tags: post.tags || [],
      author_name: post.author_name,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      status: post.status === 'archived' ? 'draft' : post.status,
    });
    // Update URL with edit mode and post ID
    const newParams = new URLSearchParams(searchParams);
    newParams.set('view', 'edit');
    newParams.set('editId', post.id.toString());
    setSearchParams(newParams, { replace: false });
  };

  const handleSave = async (publish: boolean = false) => {
    if (!form.title || !form.content) {
      setError('Title and content are required');
      return;
    }

    setSaving(true);
    setError(null);

    const payload = {
      ...form,
      status: publish ? 'published' : form.status,
    };

    try {
      const url = selectedPost
        ? `${API_URL}/admin/blog/posts/${selectedPost.id}`
        : `${API_URL}/admin/blog/posts`;
      const method = selectedPost ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify(payload),
      });

      const data: ApiResponse = await response.json();
      if (data.success) {
        setSuccess(publish ? 'Post published successfully!' : 'Post saved successfully!');
        setViewMode('list');
        fetchPosts();
      } else {
        setError(data.error || 'Failed to save post');
      }
    } catch {
      setError('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      const response = await fetch(`${API_URL}/admin/blog/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${adminToken}` },
      });

      const data: ApiResponse = await response.json();
      if (data.success) {
        setSuccess('Post deleted successfully');
        fetchPosts();
      } else {
        setError(data.error || 'Failed to delete post');
      }
    } catch {
      setError('Failed to delete post');
    }
  };

  const handleGenerate = async () => {
    if (!aiForm.topic) {
      setError('Please enter a topic');
      return;
    }

    setGenerating(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/admin/blog/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          topic: aiForm.topic,
          category: aiForm.category || null,
          tone: aiForm.tone,
          keywords: aiForm.keywords ? aiForm.keywords.split(',').map(k => k.trim()) : null,
        }),
      });

      const data: ApiResponse = await response.json();
      if (data.success && data.data) {
        setForm({
          ...form,
          title: data.data.title || '',
          content: data.data.content || '',
          excerpt: data.data.excerpt || '',
          tags: data.data.tags || [],
          category: data.data.category || aiForm.category || '',
          meta_description: data.data.meta_description || '',
        });
        setShowAiModal(false);
        setSuccess('Content generated successfully!');
      } else {
        setError(data.error || 'Failed to generate content');
      }
    } catch {
      setError('Failed to generate content');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!form.title && !form.content && !form.excerpt) {
      setError('Please add a title, content, or excerpt before generating an image');
      return;
    }

    setGeneratingImage(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/admin/blog/generate-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          title: form.title,
          content: form.content,
          excerpt: form.excerpt,
          style: 'vivid',
        }),
      });

      const data: { success: boolean; data?: { url: string }; error?: string } = await response.json();
      if (data.success && data.data?.url) {
        setForm({ ...form, featured_image: data.data.url });
        setSuccess('Featured image generated successfully!');
      } else {
        setError(data.error || 'Failed to generate image');
      }
    } catch {
      setError('Failed to generate image');
    } finally {
      setGeneratingImage(false);
    }
  };

  const addTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm({ ...form, tags: [...form.tags, tagInput.trim()] });
      setTagInput('');
    }
  };

  const removeTag = (tag: string) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-600';
      case 'draft':
        return 'bg-yellow-100 text-yellow-600';
      case 'archived':
        return 'bg-slate-100 text-slate-500';
      default:
        return 'bg-slate-100 text-slate-500';
    }
  };

  if (loading && viewMode === 'list') {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-purple-500" />
      </div>
    );
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
      className="space-y-6"
    >
      {/* Messages */}
      <AnimatePresence>
        {(error || success) && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className={`p-4 rounded-xl flex items-center gap-3 ${
              error ? 'bg-red-50 border border-red-200' : 'bg-green-50 border border-green-200'
            }`}
          >
            {error ? (
              <AlertCircle className="w-5 h-5 text-red-500" />
            ) : (
              <Check className="w-5 h-5 text-green-500" />
            )}
            <span className={error ? 'text-red-600' : 'text-green-600'}>
              {error || success}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-1 sm:mb-2">Blog Management</h1>
              <p className="text-sm sm:text-base text-slate-500">
                Create and manage blog posts with AI-powered content generation
              </p>
            </div>
            <button onClick={handleCreate} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center justify-center w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              New Post
            </button>
          </motion.div>

          {/* Filters */}
          <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search posts..."
                className="w-full h-11 pl-12 pr-4 bg-white border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
              />
            </div>
            <div className="flex gap-2">
              {['all', 'published', 'draft'].map((status) => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    filterStatus === status
                      ? 'bg-purple-500 text-white'
                      : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Posts Grid */}
          <motion.div variants={fadeInUp} className="grid gap-4">
            {filteredPosts.length === 0 ? (
              <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center shadow-sm">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Posts Yet</h3>
                <p className="text-slate-500 mb-4">
                  Create your first blog post to get started
                </p>
                <button onClick={handleCreate} className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow inline-flex items-center">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Post
                </button>
              </div>
            ) : (
              filteredPosts.map((post) => (
                <div
                  key={post.id}
                  className="bg-white rounded-2xl border border-slate-200 p-6 hover:border-purple-300 transition-colors shadow-sm"
                >
                  <div className="flex items-start gap-4">
                    {/* Thumbnail */}
                    {post.featured_image ? (
                      <img
                        src={post.featured_image}
                        alt={post.title}
                        className="w-24 h-24 rounded-xl object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <Image className="w-8 h-8 text-slate-300" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="text-lg font-semibold text-slate-900 mb-1 line-clamp-1">
                            {post.title}
                          </h3>
                          <p className="text-sm text-slate-500 line-clamp-2 mb-2">
                            {post.excerpt}
                          </p>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(post.status)}`}>
                          {post.status}
                        </span>
                      </div>

                      <div className="flex items-center gap-4 text-sm text-slate-400">
                        {post.category && (
                          <span className="flex items-center gap-1">
                            <Tag className="w-3 h-3" />
                            {post.category}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.created_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.reading_time} min read
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {post.view_count} views
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => handleEdit(post)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => window.open(`/blog/${post.slug}`, '_blank')}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                        disabled={post.status !== 'published'}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(post.id)}
                        className="p-2 rounded-lg bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </motion.div>
        </>
      )}

      {/* Create/Edit View */}
      {(viewMode === 'create' || viewMode === 'edit') && (
        <>
          {/* Header */}
          <motion.div variants={fadeInUp} className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setViewMode('list')}
                className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50 text-slate-500 hover:text-slate-700 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {viewMode === 'create' ? 'Create New Post' : 'Edit Post'}
                </h1>
                <p className="text-slate-500">
                  {viewMode === 'create' ? 'Write a new blog post' : `Editing: ${selectedPost?.title}`}
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setShowAiModal(true)}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors flex items-center"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Generate with AI
              </button>
              <button
                onClick={() => handleSave(false)}
                disabled={saving}
                className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors flex items-center"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                Save Draft
              </button>
              <button
                onClick={() => handleSave(true)}
                disabled={saving}
                className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow flex items-center"
              >
                {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                Publish
              </button>
            </div>
          </motion.div>

          {/* Editor Form */}
          <motion.div variants={fadeInUp} className="grid lg:grid-cols-3 gap-6">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Title */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Title *
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Enter post title..."
                  className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 text-lg"
                />
              </div>

              {/* Content */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Content * (Markdown supported)
                </label>
                <textarea
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  placeholder="Write your blog post content here... Use Markdown for formatting."
                  rows={20}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 font-mono text-sm resize-none"
                />
              </div>

              {/* Excerpt */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Excerpt
                </label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                  placeholder="Brief summary of the post (shown in listings)..."
                  rows={3}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 resize-none"
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Featured Image */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-slate-600">
                    Featured Image
                  </label>
                  <button
                    onClick={handleGenerateImage}
                    disabled={generatingImage}
                    className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-purple-500 to-cyan-500 text-white text-xs font-medium shadow-sm hover:shadow-md transition-shadow disabled:opacity-50 flex items-center gap-1.5"
                  >
                    {generatingImage ? (
                      <>
                        <RefreshCw className="w-3 h-3 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <ImagePlus className="w-3 h-3" />
                        Generate with AI
                      </>
                    )}
                  </button>
                </div>
                <input
                  type="url"
                  value={form.featured_image}
                  onChange={(e) => setForm({ ...form, featured_image: e.target.value })}
                  placeholder="https://... or generate with AI"
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 mb-3"
                />
                {form.featured_image ? (
                  <div className="relative">
                    <img
                      src={form.featured_image}
                      alt="Featured"
                      className="w-full h-40 object-cover rounded-xl"
                      onError={(e) => (e.currentTarget.style.display = 'none')}
                    />
                    <button
                      onClick={() => setForm({ ...form, featured_image: '' })}
                      className="absolute top-2 right-2 p-1.5 bg-red-500/80 hover:bg-red-500 rounded-lg text-white transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="w-full h-40 rounded-xl bg-slate-100 flex flex-col items-center justify-center text-slate-400">
                    <Image className="w-8 h-8 mb-2" />
                    <span className="text-xs">No image selected</span>
                  </div>
                )}
              </div>

              {/* Thumbnail Image */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Thumbnail Image URL
                </label>
                <input
                  type="url"
                  value={form.thumbnail_image}
                  onChange={(e) => setForm({ ...form, thumbnail_image: e.target.value })}
                  placeholder="https://..."
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                />
              </div>

              {/* Category */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Category
                </label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                >
                  <option value="">Select category...</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.name}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tags */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                    placeholder="Add a tag..."
                    className="flex-1 h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                  />
                  <button onClick={addTag} className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {form.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-purple-100 text-purple-600 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button onClick={() => removeTag(tag)} className="hover:text-purple-800">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Author */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <label className="block text-sm font-medium text-slate-600 mb-2">
                  Author Name
                </label>
                <input
                  type="text"
                  value={form.author_name}
                  onChange={(e) => setForm({ ...form, author_name: e.target.value })}
                  placeholder="Author name..."
                  className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                />
              </div>

              {/* SEO */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
                <h3 className="font-medium text-slate-900 mb-4">SEO Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Meta Title
                    </label>
                    <input
                      type="text"
                      value={form.meta_title}
                      onChange={(e) => setForm({ ...form, meta_title: e.target.value })}
                      placeholder="SEO title..."
                      className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-2">
                      Meta Description
                    </label>
                    <textarea
                      value={form.meta_description}
                      onChange={(e) => setForm({ ...form, meta_description: e.target.value })}
                      placeholder="SEO description (max 160 characters)..."
                      rows={2}
                      maxLength={160}
                      className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900 resize-none"
                    />
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}

      {/* AI Generation Modal */}
      <AnimatePresence>
        {showAiModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
            onClick={() => setShowAiModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl border border-slate-200 p-6 w-full max-w-lg shadow-xl"
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-slate-900">Generate with AI</h3>
                    <p className="text-sm text-slate-500">Create content using ChatGPT</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowAiModal(false)}
                  className="p-2 hover:bg-slate-100 rounded-lg text-slate-400 hover:text-slate-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Topic *
                  </label>
                  <input
                    type="text"
                    value={aiForm.topic}
                    onChange={(e) => setAiForm({ ...aiForm, topic: e.target.value })}
                    placeholder="e.g., How AI is transforming customer support"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Category
                  </label>
                  <select
                    value={aiForm.category}
                    onChange={(e) => setAiForm({ ...aiForm, category: e.target.value })}
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                  >
                    <option value="">Select category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.name}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Tone
                  </label>
                  <select
                    value={aiForm.tone}
                    onChange={(e) => setAiForm({ ...aiForm, tone: e.target.value })}
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                  >
                    <option value="professional">Professional</option>
                    <option value="casual">Casual & Friendly</option>
                    <option value="technical">Technical</option>
                    <option value="educational">Educational</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-2">
                    Keywords (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={aiForm.keywords}
                    onChange={(e) => setAiForm({ ...aiForm, keywords: e.target.value })}
                    placeholder="e.g., AI, automation, productivity"
                    className="w-full h-11 px-4 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 text-slate-900"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => setShowAiModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerate}
                  disabled={generating || !aiForm.topic}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-500 to-cyan-500 text-white font-medium shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 transition-shadow disabled:opacity-50 flex items-center"
                >
                  {generating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Content
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
